import {
  Backpack,
  Car,
  GraduationCap,
  MapPin,
  NavigationArrow,
  PencilSimple,
  Trash,
  UsersThree,
} from '@phosphor-icons/react';
import { addDays, shiftOn, type ShiftPlan } from '@shh/shared';
import { Avatar, Button, ChoiceGroup, Icon, Sheet, TaskCard, Text } from '@shh/ui';
import { useState } from 'react';
import { useLens } from '../app/lens';
import {
  deleteEvent,
  endSeriesBefore,
  endShiftPlan,
  setShiftDay,
  skipOccurrence,
  toggleEventCheck,
} from '../data/api';
import { isFamilyEvent, participants, unitFor, type E } from '../data/derive';
import { calendarDay } from '../data/calendar';
import { jobOfEntry } from '../data/jobs';
import { useFamily, type WithId } from '../data/family';
import { iconFor } from '../lib/icons';
import { dayTitleShort } from './calendar/format';
import { EventForm } from './EventForm';
import { useJobEdit } from './JobEdit';
import { ShiftForm } from './ShiftForm';
import { TimetableForm } from './TimetableForm';
import { calendarText } from '../i18n/calendar';

/**
 * Details of one event – including the day's timetable for school spans. "Bearbeiten" opens the
 * form in the same sheet, wherever the event was tapped (Heute, Kalender, Personenseite).
 */
export function EventSheet({
  eventId,
  date,
  onClose,
  onEdit,
}: {
  eventId: string | null;
  date: string;
  onClose: () => void;
  onEdit?: (e: E) => void;
}) {
  const f = useFamily();
  const lens = useLens();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingPlan, setEditingPlan] = useState(false);
  const [editingTimetable, setEditingTimetable] = useState(false);
  const e = eventId ? calendarDay(f, date).find((x) => x.id === eventId) : undefined;
  const planner = lens.canParent || f.role === 'teen';
  const editable = e && !e.virtual && e.source === 'manual' && planner;
  // a one-off job on its day (1.28): the same "Bearbeiten · Löschen" as on its card
  const editJob = useJobEdit();
  const job = e?.virtual === 'todo' ? jobOfEntry(f, e.id) : undefined;
  const jobEdit = job && editJob(job, 'edit');
  const jobDelete = job && editJob(job, 'delete');
  // the school band (1.28): parents change every timetable, teens their own (rules)
  const ttMember =
    e?.virtual === 'timetable' && e.assignees !== 'all'
      ? f.members.find((m) => m.id === e.assignees[0])
      : undefined;
  const timetableEditable = ttMember && (lens.canParent || ttMember.id === f.meId);
  // a shift of a shift plan (1.25): swap it on this day, change or end the plan
  const plan =
    e?.virtual === 'shift' ? f.shiftPlans.find((p) => p.id === e.shiftPlanId) : undefined;
  const sourceName =
    e?.virtual === 'timetable'
      ? calendarText.timetable
      : e?.source === 'waste'
        ? calendarText.waste
        : e?.source === 'ics'
          ? (f.sources.find((s) => s.id === e.sourceId)?.title ?? calendarText.calendarLink)
          : undefined;

  return (
    <Sheet
      isOpen={!!e}
      onOpenChange={(o) => {
        if (!o) {
          setConfirmDelete(false);
          setEditing(false);
          setEditingPlan(false);
          setEditingTimetable(false);
          onClose();
        }
      }}
      title={editing ? calendarText.editEvent : (e?.title ?? '')}
      isDismissable={!editing && !editingPlan && !editingTimetable}
    >
      {e && plan && editingPlan && (
        <ShiftForm
          plan={plan}
          onDone={() => {
            setEditingPlan(false);
            onClose();
          }}
        />
      )}
      {e && editing && (
        <EventForm
          initial={e}
          onDone={() => {
            setEditing(false);
            onClose();
          }}
        />
      )}
      {e && ttMember && editingTimetable && (
        <TimetableForm
          member={ttMember}
          onDone={() => {
            setEditingTimetable(false);
            onClose();
          }}
        />
      )}
      {e && !editing && !editingPlan && !editingTimetable && (
        <div className="flex flex-col gap-gap-lg">
          <div className="flex items-center gap-gap-md">
            <span className="grid size-touch-target place-items-center rounded-control bg-accent-secondary-soft text-accent-secondary">
              <Icon icon={iconFor(e.icon)} size="lg" />
            </span>
            <span className="font-display text-2xl font-black tabular-nums">
              {e.start ?? calendarText.allDay}
              {e.end ? ` – ${e.end}` : ''}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-gap-sm">
            {isFamilyEvent(e) ? (
              <span className="inline-flex items-center gap-gap-xs rounded-chip bg-accent-secondary px-inset-sm py-1 font-bold text-accent-on-accent">
                <Icon icon={UsersThree} size="sm" weight="fill" /> {calendarText.wholeFamily}
              </span>
            ) : (
              participants(f, e).map((p) => (
                <span
                  key={p.id}
                  className="inline-flex items-center gap-gap-xs rounded-chip bg-surface-sunken py-1 pr-inset-sm pl-1 font-semibold"
                >
                  <Avatar name={p.name} color={p.color} emoji={p.emoji} size="sm" decorative />
                  {p.name}
                </span>
              ))
            )}
          </div>

          {e.details?.length ? (
            <section aria-labelledby="timetable" className="flex flex-col gap-gap-sm">
              <h3
                id="timetable"
                className="flex items-center gap-gap-xs font-display text-lg font-black"
              >
                <Icon icon={GraduationCap} size="md" /> {calendarText.timetable}
              </h3>
              <ol className="grid grid-cols-1 gap-gap-xs sm:grid-cols-2">
                {e.details.map((d) => (
                  <li
                    key={d}
                    className="rounded-control bg-surface-sunken px-inset-sm py-inset-xs font-semibold"
                  >
                    {d}
                  </li>
                ))}
              </ol>
            </section>
          ) : null}

          {e.location && (
            // one tap to the route (Mike, 20.10.: "aus dem Termin dann öffnen in Google Maps")
            <div className="flex flex-wrap items-center gap-gap-sm">
              <Icon icon={MapPin} size="md" className="text-text-secondary" />
              <Text className="min-w-0 flex-1">{e.location}</Text>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.location)}`}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex min-h-touch-min items-center gap-gap-xs rounded-control font-bold text-action-primary underline underline-offset-2 outline-none focus-visible:outline-3 focus-visible:outline-border-focus"
              >
                <Icon icon={NavigationArrow} size="sm" /> {calendarText.route}
              </a>
            </div>
          )}
          {e.transport && (
            <Text className="flex items-center gap-gap-sm">
              <Icon icon={Car} size="md" className="text-text-secondary" /> {e.transport}
            </Text>
          )}
          {e.bring?.length ? (
            <div className="flex flex-wrap items-center gap-gap-sm">
              <Icon icon={Backpack} size="md" className="text-text-secondary" />
              {e.bring.map((b) => (
                <span
                  key={b}
                  className="rounded-chip bg-accent-tertiary-soft px-inset-sm py-1 font-semibold"
                >
                  {b}
                </span>
              ))}
            </div>
          ) : null}
          {e.notes && <Text tone="secondary">{e.notes}</Text>}
          {!e.virtual && !!e.stars && (date === f.today || date === addDays(f.today, -1)) && (
            <EventDone e={e} date={date} />
          )}
          {timetableEditable && (
            <Button
              variant="secondary"
              icon={PencilSimple}
              className="self-start"
              onPress={() => setEditingTimetable(true)}
            >
              {calendarText.editTimetable}
            </Button>
          )}
          {plan && planner && (
            <ShiftDay
              plan={plan}
              date={date}
              current={e.shiftKey ?? ''}
              onEdit={() => setEditingPlan(true)}
              onEnd={() => {
                void endShiftPlan(f.householdId, plan.id, date);
                onClose();
              }}
            />
          )}
          {sourceName && (
            <Text size="sm" tone="muted">
              {calendarText.from(sourceName)}
            </Text>
          )}

          {jobEdit && jobDelete && (
            <div className="flex flex-wrap gap-gap-sm">
              <Button
                variant="secondary"
                icon={PencilSimple}
                onPress={() => {
                  onClose();
                  jobEdit();
                }}
              >
                {calendarText.edit}
              </Button>
              <Button
                variant="quiet"
                icon={Trash}
                onPress={() => {
                  onClose();
                  jobDelete();
                }}
              >
                {calendarText.delete}
              </Button>
            </div>
          )}
          {editable && (
            <div className="flex flex-wrap gap-gap-sm">
              <Button
                variant="secondary"
                icon={PencilSimple}
                onPress={() => (onEdit ? onEdit(e) : setEditing(true))}
              >
                {calendarText.edit}
              </Button>
              {confirmDelete && e.repeat ? (
                // a series: only this date (Mike, 19.10.: "einen einzelnen Termin aus einer Serie
                // löschen ohne die Serie zu löschen") or all of it
                <>
                  <Button
                    variant="danger"
                    icon={Trash}
                    onPress={() => {
                      void skipOccurrence(f.householdId, e.id, date);
                      onClose();
                    }}
                  >
                    {calendarText.deleteOne(dayTitleShort(date))}
                  </Button>
                  {/* from here on: the series ends the day before (1.25) */}
                  {date > (f.events.find((x) => x.id === e.id)?.date ?? date) && (
                    <Button
                      variant="quiet"
                      icon={Trash}
                      onPress={() => {
                        void endSeriesBefore(f.householdId, e.id, date);
                        onClose();
                      }}
                    >
                      {calendarText.deleteFollowing(dayTitleShort(date))}
                    </Button>
                  )}
                  <Button
                    variant="quiet"
                    icon={Trash}
                    onPress={() => {
                      void deleteEvent(f.householdId, e.id);
                      onClose();
                    }}
                  >
                    {calendarText.deleteSeries}
                  </Button>
                </>
              ) : confirmDelete ? (
                <Button
                  variant="danger"
                  icon={Trash}
                  onPress={() => {
                    void deleteEvent(f.householdId, e.id);
                    onClose();
                  }}
                >
                  {calendarText.reallyDelete}
                </Button>
              ) : (
                <Button variant="quiet" icon={Trash} onPress={() => setConfirmDelete(true)}>
                  {calendarText.delete}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}

/** Only on this day: another shift, off or on holiday – the rhythm carries on (Mike, 19.10.). */
function ShiftDay({
  plan,
  date,
  current,
  onEdit,
  onEnd,
}: {
  plan: WithId<ShiftPlan>;
  date: string;
  current: string;
  onEdit: () => void;
  onEnd: () => void;
}) {
  const f = useFamily();
  const planned = shiftOn({ ...plan, over: undefined }, date) ?? '-';
  const name = (k: string) =>
    k === '-'
      ? calendarText.shiftFree
      : k === 'U'
        ? calendarText.vacation
        : (plan.types[k as 'F']?.name ?? k);
  const keys = [...new Set(plan.pattern.filter((k) => k !== '-'))];
  return (
    <section className="flex flex-col gap-gap-md">
      <ChoiceGroup
        label={calendarText.shiftOnlyToday}
        description={calendarText.shiftOnlyTodayHint}
        value={current}
        onChange={(k) => void setShiftDay(f.householdId, plan.id, date, k === planned ? null : k)}
        choices={[...keys, '-', 'U'].map((k) => ({ value: k, label: name(k) }))}
      />
      {plan.over?.[date] && (
        <Text size="sm" tone="muted">
          {calendarText.shiftSwapped(name(planned))}
        </Text>
      )}
      <div className="flex flex-wrap gap-gap-sm">
        <Button variant="secondary" icon={PencilSimple} onPress={onEdit}>
          {calendarText.shiftEditPlan}
        </Button>
        <Button variant="quiet" icon={Trash} onPress={onEnd}>
          {calendarText.shiftEndPlan}
        </Button>
      </div>
    </section>
  );
}

/**
 * "Erledigt?" (1.25, Mike 20.10.: "über den Termin kann man sie abhaken") – like a task, the first
 * one to tick gets the stars; the server books them (today or yesterday, only for the event's
 * people). Everyone ticks for themselves, parents and the wall for anybody.
 */
function EventDone({ e, date }: { e: E; date: string }) {
  const f = useFamily();
  const check = f.eventChecks[`${e.id}_${date}`];
  const people = participants(f, e).filter(
    (p) => f.role === 'parent' || f.role === 'device' || p.id === f.meId,
  );
  if (!people.length) return null;
  return (
    <section aria-labelledby="event-done" className="flex flex-col gap-gap-sm">
      <h3 id="event-done" className="font-display text-lg font-black">
        {calendarText.done}
      </h3>
      <ul className="flex flex-col gap-gap-xs">
        {people.map((p) => (
          <li key={p.id}>
            <TaskCard
              density="compact"
              title={p.name}
              icon={iconFor(e.icon)}
              points={e.stars}
              pointsUnit={unitFor(p)}
              accent={p.color}
              status={check?.memberId === p.id && check.status === 'done' ? 'done' : 'open'}
              // one tick per day: whoever ticked first keeps it (the rules refuse a takeover)
              isDisabled={!!check && check.memberId !== p.id}
              onStatusChange={() => void toggleEventCheck(f, e.id, date, p.id)}
            />
          </li>
        ))}
      </ul>
      <Text size="sm" tone="muted">
        {calendarText.doneHint}
      </Text>
    </section>
  );
}
